import clsx from "clsx";
import { useMemo } from "react";
import { CATEGORY_METADATA, LabelMetadata } from "../labeling.constants";
import { LabelItem } from "./LabelItem";
import {
    AccountCategory,
    AccountLabel,
} from "@/entities/DataAssociation/Account.model";

export function LabelList({
    labels,
    selectedLabel,
    onLabelSelection,
    recommendedLabels = [],
    className,
}: {
    labels: LabelMetadata[];
    recommendedLabels?: AccountLabel[];
    selectedLabel?: AccountLabel | null;
    onLabelSelection: (label: AccountLabel) => void;
    className?: string;
}) {
    const { groupedLabels, categories, hotkeyMap } = useMemo(() => {
        const groups: Record<AccountCategory, LabelMetadata[]> = {} as Record<
            AccountCategory,
            LabelMetadata[]
        >;
        labels.forEach((label) => {
            const category = label.category;
            if (!category) return;
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(label);
        });

        const cats = Object.keys(groups) as AccountCategory[];
        const hotkeys: Record<string, number> = {};
        let globalIndex = 0;
        for (const cat of cats) {
            for (const label of groups[cat]) {
                if (globalIndex < 3) {
                    hotkeys[label.code] = globalIndex + 1;
                }
                globalIndex++;
            }
        }

        return { groupedLabels: groups, categories: cats, hotkeyMap: hotkeys };
    }, [labels]);

    return (
        <div
            className={clsx(
                "pb-4",
                "hide-scrollbar flex flex-1 flex-col gap-4 overflow-y-auto",
                className,
            )}
        >
            {categories.map((category) => (
                <div key={category}>
                    <div
                        className={clsx(
                            "lozenge",
                            "flex items-center gap-4 px-3 py-4 font-medium",
                        )}
                    >
                        {CATEGORY_METADATA[category]?.name || category}
                    </div>
                    <ul role="list" className="flex flex-col gap-1">
                        {groupedLabels[category].map((item) => (
                            <LabelItem
                                dot
                                key={item.code}
                                listItem
                                label={item}
                                className={clsx(
                                    selectedLabel === item.code && "selected",
                                )}
                                onClick={() => {
                                    onLabelSelection(item.code);
                                }}
                                recommended={recommendedLabels.includes(
                                    item.code,
                                )}
                                hotkeyNumber={hotkeyMap[item.code]}
                            />
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}
